/**
 * Avatar utilities - generate colored logo variations and handle custom uploads
 */

// Color variations for default avatars
export const avatarColors = [
  { bg: '#1e7a9c', circle1: '#f4d23f', circle2: '#c9a830', name: 'blue-gold' },
  { bg: '#2d5a3f', circle1: '#7ed956', circle2: '#5eb83d', name: 'green' },
  { bg: '#7c2d5a', circle1: '#ff6b9d', circle2: '#e84a7a', name: 'pink' },
  { bg: '#b84d1e', circle1: '#ffa366', circle2: '#ff8c47', name: 'orange' },
  { bg: '#4b2d7c', circle1: '#9b7aff', circle2: '#7c5adc', name: 'purple' },
  { bg: '#2d5a7c', circle1: '#5ac8fa', circle2: '#3baae0', name: 'cyan' },
  { bg: '#7c4d2d', circle1: '#d4a574', circle2: '#b8885a', name: 'brown' },
  { bg: '#5a2d2d', circle1: '#ff6b6b', circle2: '#e84a4a', name: 'red' },
];

/**
 * Generate SVG avatar with specific colors
 */
export function generateAvatarSvg(colorIndex: number): string {
  const color = avatarColors[colorIndex % avatarColors.length];
  return `data:image/svg+xml,${encodeURIComponent(`
    <svg width="48" height="48" viewBox="0 0 768 768" xmlns="http://www.w3.org/2000/svg">
      <rect width="768" height="768" fill="${color.bg}"/>
      <circle cx="280" cy="280" r="180" fill="${color.circle1}" stroke="#000" stroke-width="8"/>
      <circle cx="450" cy="450" r="200" fill="${color.circle2}" stroke="#000" stroke-width="8"/>
    </svg>
  `)}`;
}

/**
 * Get random avatar color index based on name
 */
export function getAvatarIndexFromName(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) - hash) + name.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash) % avatarColors.length;
}

/**
 * Get default avatar for a user based on their name
 */
export function getDefaultAvatar(name: string): string {
  const index = getAvatarIndexFromName(name);
  return generateAvatarSvg(index);
}

/**
 * Compress and validate image file for avatar upload
 */
export async function processAvatarUpload(file: File): Promise<string> {
  // Validate file type
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }
  
  // Validate file size (max 500KB)
  const maxSize = 500 * 1024;
  if (file.size > maxSize) {
    throw new Error('Image must be smaller than 500KB');
  }
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Create canvas to resize
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        // Resize to 200x200
        const size = 200;
        canvas.width = size;
        canvas.height = size;
        
        // Draw image centered and cropped
        const scale = Math.max(size / img.width, size / img.height);
        const x = (size - img.width * scale) / 2;
        const y = (size - img.height * scale) / 2;
        
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
        
        // Convert to data URL (JPEG for better compression)
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        
        // Check final size
        if (dataUrl.length > maxSize * 1.5) {
          reject(new Error('Compressed image is still too large'));
          return;
        }
        
        resolve(dataUrl);
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Check if avatar is a custom uploaded image (data URL)
 */
export function isCustomAvatar(avatar: string): boolean {
  return avatar.startsWith('data:image/');
}
